'use client' // Directive for Next.js to treat this file as a client-side module

import Cookies from 'js-cookie' // Importing js-cookie library for cookie manipulation

// Define the allowed options when setting a cookie
export interface CookieOptions {
  expires?: number | Date // Expiry can be in days or a specific Date
  path?: string // URL path the cookie is valid for
  secure?: boolean // Whether cookie is only sent over HTTPS
  sameSite?: 'strict' | 'lax' | 'none' // SameSite policy for cookie sharing
}

// Interface defining all supported cookie operations
export interface CookieManager {
  set: (name: string, value: string, options?: CookieOptions) => void // Set a cookie
  get: (name: string) => string | undefined // Get a specific cookie
  remove: (name: string) => void // Remove a specific cookie
  getAll: () => Record<string, string> // Get all cookies as key-value pairs
  removeAll: () => void // Remove all cookies
  getAuthRole: () => string // Get user's role from the cookie
  getAuthName: () => string // Get user's name from the cookie
  getCapitalRole: () => string // Get role name with capitalized words (e.g., 'super_admin' → 'Super Admin')
}

// Helper function to safely parse the 'user' cookie as JSON
const safeParseUser = (): Record<string, any> => {
  const userStr = Cookies.get('user') // Get 'user' cookie
  if (!userStr) return {} // Return empty object if not present

  try {
    return JSON.parse(userStr) // Attempt to parse the JSON string
  } catch (err) {
    console.error('Invalid JSON in user cookie:', err) // Log error if JSON is malformed
    return {}
  }
}

// Main cookie utility object implementing CookieManager
export const cookieClient: CookieManager = {
  // Set a cookie with optional options
  set: (name, value, options = {}) => {
    Cookies.set(name, value, options)
  },

  // Get a specific cookie by name
  get: (name) => {
    return Cookies.get(name)
  },

  // Remove a cookie by name
  remove: (name) => {
    Cookies.remove(name)
  },

  // Get all available cookies
  getAll: () => {
    return Cookies.get()
  },

  // Remove all cookies
  removeAll: () => {
    const all = Cookies.get()
    Object.keys(all).forEach((cookieName) => {
      Cookies.remove(cookieName)
    })
  },

  // Get the user's role from the 'user' cookie
  getAuthRole: () => {
    const user = safeParseUser()
    return user?.role_name || '' // Return empty string if not found
  },

  // Get the user's name from the 'user' cookie
  getAuthName: () => {
    const user = safeParseUser()
    return user?.name || '' // Return empty string if not found
  },

  // Get capitalized version of the role name (e.g., 'super_admin' -> 'Super Admin')
  getCapitalRole: () => {
    const user = safeParseUser()
    return (
      user?.role_name
        ?.split('_') // Split on underscore
        ?.map((word:string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
        ?.join(' ') || '' // Join with space and return
    )
  },
}
